import React from "react";
import { View, Text, StyleSheet, Pressable } from "react-native";
import Svg, { Path } from "react-native-svg";
import { useNavigation } from "@react-navigation/native";
import { useTheme } from "../../../context/ThemeContext";
import { useMobile } from "../../../hooks/useMobile";
import routes from "../../../navigation/routes";
import { Reveal, WebClass, FONT_DISPLAY, FONT_MONO } from "./_shared";

// =============================================================================
// Pricing — 4 tiers (Starter / Team / Business / Enterprise). Card layout
// mirrors the Industries section so the visual rhythm is consistent. The
// "Business" tier is featured in the middle with a "MOST POPULAR" tag.
// Numbers are placeholder demo data — replace with real pricing later.
// =============================================================================

function CheckIcon({ color }) {
  return (
    <Svg width={14} height={14} viewBox="0 0 24 24">
      <Path
        stroke={color}
        strokeWidth={2.4}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M20 6L9 17l-5-5"
      />
    </Svg>
  );
}

const TIERS = [
  {
    id: "starter",
    name: "Starter",
    price: "Free",
    priceUnit: "",
    sub: "Internal-only PBX. Great for piloting Voxcraft before you add external lines.",
    cta: "Get started",
    ctaPrimary: false,
    bullets: [
      "Unlimited internal extensions",
      "Internal calls only. No PSTN in or out",
      "Voicemail + email delivery",
      "Softphone + BYO SIP devices",
      "Community support",
    ],
  },
  {
    id: "team",
    name: "Launcher",
    price: "$9",
    priceUnit: "/seat / mo",
    sub: "Small teams that need a real phone system, fast.",
    cta: "Start free trial",
    ctaPrimary: false,
    bullets: [
      "Up to 10 extensions",
      "Ring groups + IVR menus",
      "Voicemail-to-email + transcription",
      "Number porting + new DIDs",
      "Email support",
    ],
  },
  {
    id: "business",
    name: "Max",
    price: "$19",
    priceUnit: "/seat / mo",
    sub: "Most teams pick this one. Everything you need to run a department.",
    cta: "Start free trial",
    ctaPrimary: true,
    featured: true,
    bullets: [
      "Up to 50 extensions",
      "Full IVR with TTS & nested menus",
      "On-demand call recording + retention",
      "Granular RBAC + group roles",
      "AI setup assistant",
      "Priority support",
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: "Custom",
    priceUnit: "",
    sub: "Multi-tenant, hardened deployments. Cloud, on-prem, or air-gapped.",
    cta: "Talk to sales",
    ctaPrimary: false,
    bullets: [
      "Unlimited extensions & tenants",
      "Multi-tenant admin + SSO/SAML",
      "FedRAMP-aligned controls + E911",
      "On-prem appliance option",
      "99.99% SLA + dedicated CSM",
      "24/7 support",
    ],
  },
];

export default function NtVoxcraftPricing() {
  const { theme } = useTheme();
  const nav = useNavigation();
  const isMobile = useMobile();

  const goPrimary = (tier) => {
    if (tier.id === "enterprise") {
      // No dedicated sales route yet — point to learn-more, which has the
      // contact path. Swap for a /contact or mailto: when that exists.
      nav.navigate(routes.learnmore);
    } else {
      nav.navigate(routes.onboarding);
    }
  };

  return (
    <View nativeID="pricing" style={[styles.section, { paddingVertical: isMobile ? 64 : 110 }]}>
      <View style={styles.container}>
        <Reveal>
          <View style={styles.head}>
            <Text style={[styles.eyebrow, { color: theme.textMute }]}>SIMPLE PRICING</Text>
            <Text style={[styles.title, { color: theme.text }, isMobile && styles.titleMobile]}>
              Pricing that scales with you.
            </Text>
            <Text style={[styles.lede, { color: theme.textDim }]}>
              Start free, pay per seat as you grow, and move to Enterprise when
              you need multi-tenant, on-prem, or air-gapped deployments. No
              hardware required at any tier.
            </Text>
          </View>
        </Reveal>

        <View style={[styles.grid, isMobile && styles.gridMobile]}>
          {TIERS.map((t, i) => (
            <View key={t.id} style={[styles.cell, isMobile && styles.cellMobile]}>
              <Reveal delay={(i % 4) + 1} style={styles.revealFill}>
                <WebClass
                  className="voxcraft-ind-card"
                  style={[
                    styles.card,
                    { borderColor: theme.borderSoft, backgroundColor: theme.surface },
                    t.featured && { borderColor: theme.pillBorder },
                  ]}
                >
                  {t.featured && (
                    <View style={[styles.popularTag, { backgroundColor: theme.brand2 }]}>
                      <Text style={styles.popularTagText}>MOST POPULAR</Text>
                    </View>
                  )}

                  <Text style={[styles.tierName, { color: theme.text }]}>{t.name}</Text>

                  <View style={styles.priceRow}>
                    <Text
                      style={[
                        styles.price,
                        { color: t.featured ? theme.brand2 : theme.text },
                      ]}
                    >
                      {t.price}
                    </Text>
                    {!!t.priceUnit && (
                      <Text style={[styles.priceUnit, { color: theme.textMute }]}>
                        {t.priceUnit}
                      </Text>
                    )}
                  </View>

                  <Text style={[styles.tierSub, { color: theme.textDim }]}>{t.sub}</Text>

                  <Pressable
                    onPress={() => goPrimary(t)}
                    style={({ pressed }) => [
                      styles.cta,
                      t.ctaPrimary
                        ? { backgroundColor: theme.brand2, borderColor: theme.brand2 }
                        : { borderColor: theme.borderSoft, backgroundColor: theme.bg2 },
                      pressed && styles.ctaPressed,
                    ]}
                  >
                    <Text
                      style={[
                        styles.ctaText,
                        { color: t.ctaPrimary ? "#fff" : theme.text },
                      ]}
                    >
                      {t.cta}
                    </Text>
                  </Pressable>

                  <View style={[styles.bullets, { borderTopColor: theme.borderSoft }]}>
                    {t.bullets.map((b, j) => (
                      <View key={j} style={styles.bulletRow}>
                        <View style={styles.bulletCheck}>
                          <CheckIcon color={theme.green} />
                        </View>
                        <Text style={[styles.bulletText, { color: theme.textDim }]}>{b}</Text>
                      </View>
                    ))}
                  </View>
                </WebClass>
              </Reveal>
            </View>
          ))}
        </View>

        <Reveal>
          <Text style={[styles.fineprint, { color: theme.textMute }]}>
            Prices in USD. Billed monthly or annually. Cancel anytime.
            Enterprise pricing varies by deployment model. Talk to sales for a quote.
          </Text>
        </Reveal>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  section: { paddingHorizontal: 28 },
  container: { maxWidth: 1280, marginHorizontal: "auto", width: "100%" },
  head: { maxWidth: 760, marginBottom: 56 },
  eyebrow: { fontFamily: FONT_MONO, fontSize: 11, fontWeight: "700", letterSpacing: 1.6, marginBottom: 12, textTransform: "uppercase" },
  title: { fontFamily: FONT_DISPLAY, fontSize: 42, fontWeight: "800", letterSpacing: -1, lineHeight: 46, marginBottom: 16 },
  titleMobile: { fontSize: 32, lineHeight: 36 },
  lede: { fontSize: 17, lineHeight: 27 },

  // alignItems:stretch is the default for a flex row; explicit here so the
  // intent is obvious and so the cells get the full row height to share.
  grid: { flexDirection: "row", flexWrap: "wrap", alignItems: "stretch", marginHorizontal: -8 },
  gridMobile: { flexDirection: "column", marginHorizontal: 0 },
  cell: { width: "25%", padding: 8 },
  cellMobile: { width: "100%", padding: 0, paddingBottom: 12 },
  // Reveal needs flex:1 to fill the cell vertically; otherwise its View
  // shrinks to content height and the card's `flex:1` has nothing to grow
  // into — pricing cards end up at their natural heights and look ragged.
  revealFill: { flex: 1, width: "100%" },

  card: {
    padding: 26, borderRadius: 12, borderWidth: 1,
    flex: 1, position: "relative", overflow: "hidden",
  },
  popularTag: {
    position: "absolute", top: 16, right: 16,
    paddingHorizontal: 9, paddingVertical: 4, borderRadius: 999,
  },
  popularTagText: { color: "#fff", fontSize: 9, fontWeight: "800", letterSpacing: 0.7 },

  tierName: { fontFamily: FONT_DISPLAY, fontSize: 16, fontWeight: "700", letterSpacing: -0.2, marginBottom: 12, textTransform: "uppercase" },

  priceRow: { flexDirection: "row", alignItems: "baseline", marginBottom: 10 },
  price: { fontFamily: FONT_DISPLAY, fontSize: 40, fontWeight: "800", letterSpacing: -1.4, lineHeight: 42 },
  priceUnit: { fontFamily: FONT_MONO, fontSize: 11, marginLeft: 6, letterSpacing: 0.4 },

  // minHeight fits ~3 lines of description so the CTA aligns across cards.
  tierSub: { fontSize: 13.5, lineHeight: 21, marginBottom: 20, minHeight: 72 },

  cta: {
    paddingHorizontal: 16, paddingVertical: 11,
    borderRadius: 8, borderWidth: 1,
    alignItems: "center", justifyContent: "center",
    marginBottom: 22,
  },
  ctaPressed: { opacity: 0.85, transform: [{ translateY: -1 }] },
  ctaText: { fontSize: 13, fontWeight: "600" },

  // marginTop:auto anchors the bullets to the bottom of the card so the
  // CTA stays at a consistent vertical position when cards have unequal
  // bullet counts.
  bullets: { borderTopWidth: 1, paddingTop: 16, marginTop: "auto" },
  bulletRow: { flexDirection: "row", alignItems: "flex-start", paddingVertical: 5 },
  bulletCheck: { width: 18, height: 18, alignItems: "center", justifyContent: "center", marginRight: 8, marginTop: 2 },
  bulletText: { fontSize: 13, lineHeight: 20, flex: 1 },

  fineprint: {
    fontFamily: FONT_MONO, fontSize: 11, letterSpacing: 0.3,
    textAlign: "center", marginTop: 32, maxWidth: 760, alignSelf: "center",
  },
});
